import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Instagram,
  Linkedin,
  X,
  type LucideIcon,
} from "lucide-react";

import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import IconButton from "@/blueprint/components/IconButton/IconButton";
import Text from "@/blueprint/components/Text/Text";
import Section from "@/blueprint/components/Section/Section";
import CardStack, {
  useCardStack,
} from "@/blueprint/components/CardStack/CardStack";
import { useTrack, type SocialNetwork } from "@/analytics/events";
import {
  SOCIAL_CAROUSEL,
  type SocialNetworkId,
} from "@/views/shop/shopContent";

const SOCIAL_ICONS: Record<SocialNetworkId, LucideIcon> = {
  x: X,
  instagram: Instagram,
  linkedin: Linkedin,
};

// Separate so its `useTrack` sits below the section context Section provides.
const SocialLinks = () => {
  const track = useTrack();

  return (
    <div className="flex items-center gap-s">
      {SOCIAL_CAROUSEL.links.map((link) => {
        const Icon = SOCIAL_ICONS[link.id];

        return (
          <IconButton
            key={link.id}
            variant="secondary-light"
            href={link.href}
            external
            title={link.title}
            label={link.title}
            onClick={() =>
              track("cta_clicked", {
                cta: "social",
                href: link.href,
                network: link.id as SocialNetwork,
              })
            }
          >
            <Icon aria-hidden className="h-5 w-5" />
          </IconButton>
        );
      })}
    </div>
  );
};

const SocialCarousel = ({ position }: { position: number }) => {
  const stack = useCardStack();

  return (
    <Section
      id="social"
      position={position}
      className="flex flex-col items-center gap-full px-grid-margin py-page text-center"
    >
      <div className="grid w-full max-w-[356px] grid-cols-[auto_minmax(0,408px)_auto] items-center justify-center gap-xs tab:max-w-[551px] tab:gap-5xl sd:max-w-[592px] d:max-w-[680px]">
        <IconButton
          variant="secondary-light"
          label="Previous photo"
          onClick={stack.previous}
          className="hidden bg-surface tab:flex"
        >
          <ArrowLeft aria-hidden className="h-6 w-6" />
        </IconButton>
        <CardStack
          count={SOCIAL_CAROUSEL.photos.length}
          index={stack.index}
          direction={stack.direction}
          onAdvance={stack.next}
          advanceLabel="Show next photo"
          // col-start-2 pins the stack to the middle track: the arrows are
          // display:none on mobile and vacate their grid cells, which would
          // otherwise slide the stack into the first (content-sized) column.
          className="col-start-2 w-[240px] justify-self-center [aspect-ratio:3/4] tab:w-[279px] sd:w-[320px] d:w-[408px]"
          renderCard={(photoIndex, isActive) => {
            // In range by construction: CardStack wraps the index it passes.
            const photo = SOCIAL_CAROUSEL.photos[photoIndex]!;

            return (
              <Image
                src={photo.image}
                alt={isActive ? photo.alt : ""}
                fill
                sizes="(min-width: 1600px) 408px, (min-width: 800px) 279px, 65vw"
                className="object-cover"
              />
            );
          }}
        />
        <IconButton
          variant="secondary-light"
          label="Next photo"
          onClick={stack.next}
          className="hidden bg-surface tab:flex"
        >
          <ArrowRight aria-hidden className="h-6 w-6" />
        </IconButton>
      </div>
      <div className="flex max-w-4xl flex-col items-center gap-half">
        <Link
          href={SOCIAL_CAROUSEL.eyebrowHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Eyebrow variant="eyebrow-sm" as="span">
            {SOCIAL_CAROUSEL.eyebrow}
          </Eyebrow>
        </Link>
        <Text variant="h3" as="h2" className="tab:text-h2 sd:text-h3">
          {SOCIAL_CAROUSEL.title}
        </Text>
        <SocialLinks />
      </div>
    </Section>
  );
};

export default SocialCarousel;
